/**
 * Today's date as a local (not UTC) `YYYY-MM-DD` string, suitable as the
 * default value of a native `<input type="date">`/date form control. Built
 * from local getters (not `toISOString()`, which is UTC and can roll the
 * date over near midnight) so it always matches the user's own calendar day.
 */
export const getTodayIsoDate = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${today.getFullYear()}-${month}-${day}`;
};

/**
 * Turns a `YYYY-MM-DD` calendar date into the `Date.toDateString()` text the
 * payment `date` columns store, built from the calendar parts:
 * `new Date('YYYY-MM-DD')` parses as UTC midnight, which in a negative
 * offset (Colombia, UTC-5) reads back as the previous day.
 */
export const toStoredDateString = (calendarDate: string): string => {
  const [year, month, day] = calendarDate.split('-').map(Number);

  return new Date(year, month - 1, day).toDateString();
};

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('es-CO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Formats a date as `09 jul 2026`.
 *
 * Legacy tables store dates as free text (`treasury.date` is a STRING column
 * holding whatever `Date.toDateString()` produced), so anything unparseable
 * is returned untouched rather than rendered as "Invalid Date".
 */
export function formatShortDate(
  value: string | Date | null | undefined,
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const date = value instanceof Date ? value : parseDateOnly(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : '';
  }

  return buildShortDate(date);
}

/**
 * Joins day, month and year by hand: `Intl.format` renders es-CO as
 * "20 de ago de 2026", and the design asks for "20 ago 2026".
 */
function buildShortDate(date: Date): string {
  const parts = SHORT_DATE_FORMATTER.formatToParts(date);
  const partValue = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value.replace(/\./g, '') ?? '';

  return `${partValue('day')} ${partValue('month')} ${partValue('year')}`;
}

/**
 * Reads the calendar day the string names, ignoring any time zone shift: a
 * date-only value is pinned to UTC so it cannot render as the previous day.
 */
function parseDateOnly(value: string): Date {
  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (isoDateOnly) {
    // An ISO date-only string parses as UTC midnight, which reads as the
    // previous day in any negative-offset zone, Bogotá included.
    return new Date(
      Date.UTC(
        Number(isoDateOnly[1]),
        Number(isoDateOnly[2]) - 1,
        Number(isoDateOnly[3]),
      ),
    );
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()),
  );
}
